import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Tooltip, IconButton,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import DeleteIcon from '@mui/icons-material/Delete';
import apiClient from '../api/client';

const PROPERTY_TYPES = ['house', 'apartment', 'condo', 'commercial'];
const STATUS_COLORS = { available: 'success', pending: 'warning', sold: 'error' };

const EMPTY_FORM = {
  address: '', city: '', state: '', zip_code: '',
  property_type: 'house', bedrooms: '', bathrooms: '',
  square_feet: '', price: '', description: '',
  status: 'available',
};

// Parse a quoted CSV line: "a","b","c" → ['a', 'b', 'c']
function parseCSVLine(line) {
  const fields = [];
  let i = 0;
  while (i < line.length) {
    if (line[i] === '"') {
      let val = '';
      i++; // skip opening quote
      while (i < line.length) {
        if (line[i] === '"' && line[i + 1] === '"') { val += '"'; i += 2; }
        else if (line[i] === '"') { i++; break; }
        else { val += line[i++]; }
      }
      fields.push(val);
      if (line[i] === ',') i++;
    } else {
      let val = '';
      while (i < line.length && line[i] !== ',') val += line[i++];
      if (line[i] === ',') i++;
      fields.push(val);
    }
  }
  return fields;
}

const parseMoney = s => parseFloat((s || '').replace(/[$,]/g, '')) || null;

const MLS_TYPE_MAP = { DET: 'house', ATT: 'house', CON: 'condo', APT: 'apartment' };
const MLS_STATUS_MAP = { A: 'available', P: 'pending', S: 'sold', C: 'sold' };

function mlsRowToProperty(cols) {
  // cols indices match the header order
  const mlsNum = cols[1];
  const typeCode = cols[2];
  const price = parseMoney(cols[4]);
  const pricePerSqft = parseMoney(cols[5]);
  const statusCode = cols[6];
  const address = cols[8];
  const subdivision = cols[9];
  const city = cols[10];
  const county = cols[11]; // e.g. "Fulton - GA"
  const bedrooms = parseInt(cols[12]) || null;
  const fullBaths = parseInt(cols[13]) || 0;
  const halfBaths = parseInt(cols[14]) || 0;
  const yearBuilt = cols[15];

  const stateMatch = county.match(/[-–]\s*([A-Z]{2})\s*$/);
  const state = stateMatch ? stateMatch[1] : '';

  const squareFeet = (price && pricePerSqft) ? Math.round(price / pricePerSqft) : null;
  const bathrooms = fullBaths + halfBaths * 0.5 || null;

  const descParts = [];
  if (subdivision && subdivision !== 'None' && subdivision !== 'NA' && subdivision !== 'N/A') descParts.push(subdivision);
  if (yearBuilt) descParts.push(`Built ${yearBuilt}`);

  return {
    address,
    city,
    state,
    property_type: MLS_TYPE_MAP[typeCode] || 'house',
    status: MLS_STATUS_MAP[statusCode] || 'available',
    price,
    bedrooms,
    bathrooms,
    square_feet: squareFeet,
    mls_number: mlsNum || null,
    description: descParts.join(' · ') || null,
  };
}

function parseMLSCsv(text) {
  const lines = text.trim().split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return [];
  // Skip header row (index 0)
  return lines.slice(1).map(line => mlsRowToProperty(parseCSVLine(line)));
}

const PropertyList = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Import state
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileRef = useRef();

  const handleDelete = async () => {
    await apiClient.delete(`/properties/${deleteTarget.id}`);
    setDeleteTarget(null);
    fetchProperties();
  };

  const fetchProperties = () => {
    setLoading(true);
    apiClient.get('/properties')
      .then(res => setProperties(res.data.data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchProperties(); }, []);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await apiClient.post('/properties', form);
      setDialogOpen(false);
      setForm(EMPTY_FORM);
      fetchProperties();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const rows = parseMLSCsv(ev.target.result);
        setImportRows(rows);
        setImportResult(null);
        setImportOpen(true);
      } catch (err) {
        setError('Failed to parse CSV: ' + err.message);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await apiClient.post('/properties/import', { properties: importRows });
      setImportResult(res.data);
      fetchProperties();
    } catch (err) {
      setError(err.message);
    } finally {
      setImporting(false);
    }
  };

  const handleGeocodeMissing = async () => {
    setGeocoding(true);
    try {
      const res = await apiClient.post('/properties/geocode-missing');
      const { updated, failed, total } = res.data;
      setError(null);
      alert(`Geocoded ${updated} of ${total} properties.${failed ? ` ${failed} could not be resolved.` : ''}`);
      fetchProperties();
    } catch (err) {
      setError(err.message);
    } finally {
      setGeocoding(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Properties</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFileChange} />
          <Tooltip title="Geocode all properties missing coordinates">
            <Button variant="outlined" startIcon={geocoding ? <CircularProgress size={16} /> : <MyLocationIcon />} onClick={handleGeocodeMissing} disabled={geocoding}>
              Geocode Missing
            </Button>
          </Tooltip>
          <Tooltip title="Import from MLS CSV">
            <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => fileRef.current.click()}>
              Import CSV
            </Button>
          </Tooltip>
          <Button variant="contained" onClick={() => setDialogOpen(true)}>Add Property</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Address</TableCell>
                <TableCell>MLS#</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Beds / Baths</TableCell>
                <TableCell>Sq Ft</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Visited by</TableCell>
                <TableCell>Coords</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {properties.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">No properties yet — add one above.</TableCell>
                </TableRow>
              ) : properties.map(p => {
                const uniqueVisitors = [...new Map((p.visits ?? []).map(v => [v.client_id, v])).values()];
                return (
                <TableRow key={p.id} hover>
                  <TableCell>{p.address}, {p.city}, {p.state} {p.zip_code}</TableCell>
                  <TableCell>{p.mls_number || '—'}</TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{p.property_type}</TableCell>
                  <TableCell>{p.bedrooms ?? '—'} / {p.bathrooms ?? '—'}</TableCell>
                  <TableCell>{p.square_feet ? Number(p.square_feet).toLocaleString() : '—'}</TableCell>
                  <TableCell>{p.price ? `$${Number(p.price).toLocaleString()}` : '—'}</TableCell>
                  <TableCell>
                    <Chip label={p.status} color={STATUS_COLORS[p.status]} size="small" />
                  </TableCell>
                  <TableCell>
                    {uniqueVisitors.length === 0 ? <Typography variant="body2" color="text.secondary">—</Typography> : (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {uniqueVisitors.map(v => (
                          <Chip key={v.client_id} label={`${v.client?.first_name} ${v.client?.last_name}`} size="small" color="success" variant="outlined" />
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {p.latitude && p.longitude
                      ? <Chip label="✓" color="success" size="small" />
                      : <Chip label="no coords" color="warning" size="small" />}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Delete property">
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(p)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );})}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete property?</DialogTitle>
        <DialogContent>
          <Typography>{deleteTarget?.address}, {deleteTarget?.city}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will also remove it from any client assignments and saved plans.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* CSV Import preview dialog */}
      <Dialog open={importOpen} onClose={() => { setImportOpen(false); setImportRows([]); setImportResult(null); }} maxWidth="lg" fullWidth>
        <DialogTitle>Import MLS Properties ({importRows.length} rows)</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {importResult ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="success">
                Import complete: <strong>{importResult.created}</strong> created
                {importResult.geocoded > 0 && <> (<strong>{importResult.geocoded}</strong> geocoded)</>},
                {' '}<strong>{importResult.skipped}</strong> skipped (already exist).
              </Alert>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Address</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>ST</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Bed</TableCell>
                    <TableCell>Bath</TableCell>
                    <TableCell>Sq Ft</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importRows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>{r.address}</TableCell>
                      <TableCell>{r.city}</TableCell>
                      <TableCell>{r.state}</TableCell>
                      <TableCell>{r.property_type}</TableCell>
                      <TableCell>{r.bedrooms ?? '—'}</TableCell>
                      <TableCell>{r.bathrooms ?? '—'}</TableCell>
                      <TableCell>{r.square_feet ? r.square_feet.toLocaleString() : '—'}</TableCell>
                      <TableCell>{r.price ? `$${r.price.toLocaleString()}` : '—'}</TableCell>
                      <TableCell>
                        <Chip label={r.status} color={STATUS_COLORS[r.status]} size="small" />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Tooltip title={r.description || ''}>
                          <span>{r.description}</span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setImportOpen(false); setImportRows([]); setImportResult(null); }}>
            {importResult ? 'Close' : 'Cancel'}
          </Button>
          {!importResult && (
            <Button variant="contained" onClick={handleImport} disabled={importing || importRows.length === 0}>
              {importing ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
              Import {importRows.length} Properties
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Add property dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Property</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Address" name="address" value={form.address} onChange={handleChange} required />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="City" name="city" value={form.city} onChange={handleChange} required sx={{ flex: 2 }} />
            <TextField label="State" name="state" value={form.state} onChange={handleChange} required sx={{ flex: 1 }} />
            <TextField label="ZIP" name="zip_code" value={form.zip_code} onChange={handleChange} sx={{ flex: 1 }} />
          </Box>
          <TextField select label="Type" name="property_type" value={form.property_type} onChange={handleChange}>
            {PROPERTY_TYPES.map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>)}
          </TextField>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Bedrooms" name="bedrooms" type="number" value={form.bedrooms} onChange={handleChange} sx={{ flex: 1 }} />
            <TextField label="Bathrooms" name="bathrooms" type="number" value={form.bathrooms} onChange={handleChange} sx={{ flex: 1 }} />
            <TextField label="Sq Ft" name="square_feet" type="number" value={form.square_feet} onChange={handleChange} sx={{ flex: 1 }} />
          </Box>
          <TextField label="Price" name="price" type="number" value={form.price} onChange={handleChange} />
          <TextField label="Description" name="description" value={form.description} onChange={handleChange} multiline rows={3} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving || !form.address || !form.city || !form.state}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PropertyList;
